import styled from "@emotion/styled";

import {
  MDSBadge,
  MDSBadgeVariant,
  MDSButton,
  MDSButtonSize,
  MDSButtonSlotName,
  MDSButtonVariant,
  MDSCard,
  MDSCardVariant,
  MDSIcon,
  MDSIconKind,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import type { MemoryManifest, MemoryOverview, MemoryType } from "@/pages/agent/memory/memory-api";

const ACTIVE_RUN_STATUSES = new Set(["queued", "running"]);

export const MemoryHome = ({
  overview,
  isStartingCustomerIntelligence,
  onCreate,
  onCustomize,
  onOpenManifest,
  onOpenType,
  onStartCustomerIntelligence,
}: {
  overview: MemoryOverview;
  isStartingCustomerIntelligence: boolean;
  onCreate: () => void;
  onCustomize: () => void;
  onOpenManifest: (manifestId: string) => void;
  onOpenType: (typeKey: string) => void;
  onStartCustomerIntelligence: () => void;
}) => {
  const manifests = overview.manifests;
  const entityTypes = overview.types.filter(type => type.kind === "entity");
  const relationshipTypes = overview.types.filter(type => type.kind === "relationship");
  const entityTypeById = new Map(entityTypes.map(type => [type.id, type]));
  const topLevelEntityTypes = entityTypes.filter(type => !type.parent_type_id);
  const browseableTypeKeys = new Set(
    overview.views
      .map(view => view.query.type_key)
      .filter((typeKey): typeKey is string => typeof typeKey === "string")
  );

  return (
    <HomeRoot>
      <HomeHeader>
        <HomeCopy>
          <MDSText as="h1" variant={MDSTextVariant.HeaderXl}>
            Memory
          </MDSText>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
            Living views Mem Agent builds from your notes and keeps connected as your world changes.
          </MDSText>
        </HomeCopy>
        <HeaderActions>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Chrome}
            onPress={onCustomize}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
            Change Memory
          </MDSButton>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Emphasis}
            onPress={onCreate}
          >
            <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Plus} />
            New view
          </MDSButton>
        </HeaderActions>
      </HomeHeader>

      {manifests.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <MDSText as="h2" variant={MDSTextVariant.HeaderM}>
                Views
              </MDSText>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                Each view can reuse the same canonical people, places, ideas, and relationships.
              </MDSText>
            </div>
          </SectionHeader>
          <ViewGrid>
            {manifests.map(manifest => (
              <MemoryViewCard
                key={manifest.id}
                manifest={manifest}
                viewCount={
                  overview.views.filter(
                    view => view.manifest_id === manifest.id && view.id !== manifest.root_view_id
                  ).length
                }
                onOpen={() => onOpenManifest(manifest.id)}
              />
            ))}
            <NewViewCard type="button" onClick={onCreate}>
              <MDSIcon kind={MDSIconKind.Plus} size={16} />
              <div>
                <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
                  Create another view
                </MDSText>
                <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                  Tell Mem Agent what you want to keep track of.
                </MDSText>
              </div>
            </NewViewCard>
          </ViewGrid>
        </Section>
      ) : (
        <EmptyState>
          <EmptyIcon aria-hidden="true">
            <MDSIcon kind={MDSIconKind.Cells} size={28} />
          </EmptyIcon>
          <div>
            <MDSText as="h1" variant={MDSTextVariant.HeaderL}>
              What should Memory keep current for you?
            </MDSText>
            <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphSm}>
              Start from a suggested view or describe your own. Mem Agent designs the schema,
              backfills your notes, and maintains it as new information arrives.
            </MDSText>
          </div>
          <EmptyActions>
            <MDSButton
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Emphasis}
              onPress={onCreate}
            >
              <MDSIcon slot={MDSButtonSlotName.LeftIcon} kind={MDSIconKind.Sparkle} />
              Describe what to track
            </MDSButton>
            <MDSButton
              size={MDSButtonSize.Large}
              variant={MDSButtonVariant.Chrome}
              isLoading={isStartingCustomerIntelligence}
              onPress={onStartCustomerIntelligence}
            >
              Start with Customer Intelligence
            </MDSButton>
          </EmptyActions>
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
            Examples: people, recipes, trips, restaurants, accounts, startup ideas, or any domain
            represented in your notes.
          </MDSText>
        </EmptyState>
      )}

      {entityTypes.length > 0 ? (
        <Section>
          <SectionHeader>
            <div>
              <MDSText as="h2" variant={MDSTextVariant.HeaderM}>
                Types
              </MDSText>
              <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS}>
                Canonical objects are shared across views. Subtypes represent real is-a
                relationships, not folders. Use Change Memory to add or adjust the schema.
              </MDSText>
            </div>
          </SectionHeader>
          <DirectoryGrid>
            <DirectoryGroup>
              <DirectoryLabel>Entities</DirectoryLabel>
              <TypeList>
                {topLevelEntityTypes.map(type => (
                  <EntityTypeBranch
                    key={type.id}
                    allTypes={entityTypes}
                    browseableTypeKeys={browseableTypeKeys}
                    depth={0}
                    type={type}
                    onOpenType={onOpenType}
                  />
                ))}
              </TypeList>
            </DirectoryGroup>
            {relationshipTypes.length > 0 ? (
              <DirectoryGroup>
                <DirectoryLabel>Relationships</DirectoryLabel>
                <TypeList>
                  {relationshipTypes.map(type => {
                    const source = type.source_type_id
                      ? entityTypeById.get(type.source_type_id)
                      : null;
                    const target = type.target_type_id
                      ? entityTypeById.get(type.target_type_id)
                      : null;
                    return (
                      <TypeRow
                        key={type.id}
                        $isInteractive={browseableTypeKeys.has(type.key)}
                        disabled={!browseableTypeKeys.has(type.key)}
                        type="button"
                        onClick={() => onOpenType(type.key)}
                      >
                        <TypeIcon>
                          <MDSIcon kind={MDSIconKind.Link} size={14} />
                        </TypeIcon>
                        <TypeCopy>
                          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
                            {type.plural_display_name}
                          </MDSText>
                          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
                            {source && target
                              ? `${source.display_name} → ${target.display_name}`
                              : type.description}
                          </MDSText>
                        </TypeCopy>
                        {browseableTypeKeys.has(type.key) ? (
                          <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
                        ) : null}
                      </TypeRow>
                    );
                  })}
                </TypeList>
              </DirectoryGroup>
            ) : null}
          </DirectoryGrid>
        </Section>
      ) : null}
    </HomeRoot>
  );
};

const MemoryViewCard = ({
  manifest,
  viewCount,
  onOpen,
}: {
  manifest: MemoryManifest;
  viewCount: number;
  onOpen: () => void;
}) => {
  const activeRun =
    manifest.latest_run && ACTIVE_RUN_STATUSES.has(manifest.latest_run.status)
      ? manifest.latest_run
      : null;
  const failed = manifest.latest_run?.status === "failed";
  const statusLabel = memoryViewStatusLabel({
    isBuilding: Boolean(activeRun),
    failed,
    paused: manifest.status === "paused",
  });

  return (
    <ViewCard variant={MDSCardVariant.Chrome} onPress={onOpen}>
      <ViewCardHeader>
        <ViewIcon>
          <MDSIcon kind={MDSIconKind.Cells} size={17} />
        </ViewIcon>
        <MDSBadge
          label={statusLabel}
          variant={failed ? MDSBadgeVariant.Negative : MDSBadgeVariant.Default}
        />
      </ViewCardHeader>
      <ViewCardCopy>
        <MDSText variant={MDSTextVariant.TextM} weight={MDSTextWeight.SemiBold}>
          {manifest.display_name}
        </MDSText>
        <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextS} numberOfLines={2}>
          {manifest.description}
        </MDSText>
      </ViewCardCopy>
      <ViewCardFooter>
        <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
          {viewCount} nested view{viewCount === 1 ? "" : "s"}
        </MDSText>
        <MDSIcon kind={MDSIconKind.AngleRight} size={12} />
      </ViewCardFooter>
    </ViewCard>
  );
};

const memoryViewStatusLabel = ({
  isBuilding,
  failed,
  paused,
}: {
  isBuilding: boolean;
  failed: boolean;
  paused: boolean;
}): string => {
  if (isBuilding) return "Building";
  if (failed) return "Needs another pass";
  if (paused) return "Paused";
  return "Current";
};

const EntityTypeBranch = ({
  type,
  allTypes,
  browseableTypeKeys,
  depth,
  onOpenType,
}: {
  type: MemoryType;
  allTypes: MemoryType[];
  browseableTypeKeys: Set<string>;
  depth: number;
  onOpenType: (typeKey: string) => void;
}) => {
  const children = allTypes.filter(candidate => candidate.parent_type_id === type.id);
  const isBrowseable = browseableTypeKeys.has(type.key);

  return (
    <>
      <TypeRow
        $depth={depth}
        $isInteractive={isBrowseable}
        disabled={!isBrowseable}
        type="button"
        onClick={() => onOpenType(type.key)}
      >
        <TypeIcon>
          <MDSIcon kind={MDSIconKind.Cells} size={14} />
        </TypeIcon>
        <TypeCopy>
          <MDSText variant={MDSTextVariant.TextS} weight={MDSTextWeight.SemiBold}>
            {type.plural_display_name}
          </MDSText>
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextXs}>
            {type.description}
          </MDSText>
        </TypeCopy>
        <MDSBadge
          label={(type.entity_count ?? 0).toLocaleString()}
          variant={MDSBadgeVariant.Default}
        />
        {isBrowseable ? <MDSIcon kind={MDSIconKind.AngleRight} size={12} /> : null}
      </TypeRow>
      {children.map(child => (
        <EntityTypeBranch
          key={child.id}
          allTypes={allTypes}
          browseableTypeKeys={browseableTypeKeys}
          depth={depth + 1}
          type={child}
          onOpenType={onOpenType}
        />
      ))}
    </>
  );
};

const HomeRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[8]};
`;

const HomeHeader = styled.header`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;

  @media (max-width: 620px) {
    flex-direction: column;
  }
`;

const HomeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  max-width: 680px;
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[2]};

  @media (max-width: 620px) {
    flex-wrap: wrap;
  }
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
`;

const SectionHeader = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;

  > div {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }
`;

const ViewGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const ViewCard = styled(MDSCard)`
  align-items: stretch;
  background: ${({ theme }) => theme.colors.background.card.default};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: 180px;
  padding: ${({ theme }) => theme.spacing[5]};
  text-align: left;

  &[data-hovered] {
    background: ${({ theme }) => theme.colors.background.card.emphasis};
  }
`;

const ViewCardHeader = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ViewIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  height: 34px;
  justify-content: center;
  width: 34px;
`;

const ViewCardCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const ViewCardFooter = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const NewViewCard = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 1px dashed ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  color: inherit;
  cursor: pointer;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  min-height: 180px;
  padding: ${({ theme }) => theme.spacing[5]};
  text-align: left;

  > div {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.background.card.emphasis};
  }
`;

const EmptyState = styled.section`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[5]};
  margin: auto;
  max-width: 720px;
  padding: ${({ theme }) => theme.spacing[8]} 0;
  text-align: center;

  > div:nth-of-type(2) {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
  }
`;

const EmptyIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  display: flex;
  height: 68px;
  justify-content: center;
  width: 68px;
`;

const EmptyActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: center;
`;

const DirectoryGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[4]};
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const DirectoryGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const DirectoryLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TypeList = styled.div`
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TypeRow = styled.button<{ $depth?: number; $isInteractive: boolean }>`
  align-items: center;
  appearance: none;
  background: ${({ theme }) => theme.colors.background.card.default};
  border: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.surface};
  color: inherit;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
  display: flex;
  font: inherit;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  padding-left: ${({ theme, $depth = 0 }) => `calc(${theme.spacing[3]} + ${$depth * 20}px)`};

  &:last-child {
    border-bottom: 0;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.card.emphasis};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.emphasis};
    outline-offset: -2px;
  }
`;

const TypeIcon = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.button.toggle.default};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  display: flex;
  height: 28px;
  justify-content: center;
  width: 28px;
`;

const TypeCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;
